import { useForm } from "react-hook-form";
import { useState, useRef } from "react";
import { Tooltip } from "@mui/material";
import { IoIosInformationCircleOutline } from "react-icons/io";

import {
  StyledFileInput,
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel, 
  StyledUploadLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import { ErrorText } from "../../StyledCss/styledComponents";


const BillingDetails = ({
  onSubmitData,
  closeBtn,
  isLoading,
  defaultValues,
  isEditMode,
  errorMsg,
  canEdit = true,
}) => {
  const [logoPreview, setLogoPreview] = useState(defaultValues?.logo_url || "");
  const [signaturePreview, setSignaturePreview] = useState(defaultValues?.signature_url || "");
  const [logoFile, setLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const logoInputRef = useRef(null);
  const signatureInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },

  } = useForm({
    defaultValues: defaultValues || {},
  });

  const onLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setLogoError(false);
    }
  };

  const handleCancelLogo = (e) => {
    e.preventDefault();
    setLogoFile(null);
    setLogoPreview("");
    setValue("service_provider_logo", null);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const onSignatureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSignatureFile(file);
      setSignaturePreview(URL.createObjectURL(file));
      setSignatureError(false);
    }
  };

  const handleCancelSignature = (e) => {
    e.preventDefault();
    setSignatureFile(null);
    setSignaturePreview("");
    setValue("service_provider_signature", null);
    if (signatureInputRef.current) {
      signatureInputRef.current.value = "";
    }
  };

  const validateAndSubmit = async () => {
    const hasLogo = logoFile || logoPreview;
    const hasSignature = signatureFile || signaturePreview;
    
    setLogoError(!hasLogo);
    setSignatureError(!hasSignature);
    
    await handleSubmit(onSubmit)();
  };

  const onSubmit = (formData) => {
    const hasLogo = logoFile || logoPreview;
    const hasSignature = signatureFile || signaturePreview;
    
    if (!hasLogo || !hasSignature) return;
    
    const payload = new FormData();
    
    const dataToSend = isEditMode 
      ? Object.keys(formData).reduce((changes, field) => {
          if (formData[field] !== defaultValues?.[field]) {
            changes[field] = formData[field];
          }
          return changes;
        }, {})
      : formData;
    
    Object.entries(dataToSend).forEach(([field, value]) => {
      if (value !== null && value !== undefined) payload.append(field, value);
    });
    
    [[logoFile, "logo_url"], [signatureFile, "signature_url"]]
      .forEach(([file, key]) => file && payload.append(key, file));
    
    onSubmitData(payload);
  };

  return (
    <div className="popup-content">
      <div className="add-employee-form">
      <h5 style={{ marginBottom: "15px" }}>Service Provider  Details</h5>
      <div className="add-employee-form-container">
         {/* Service Provider Name */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Service Provider Name"
                type="text"
                {...register("service_provider_name",{ required: true })}
                style={{
                  ...(errors.service_provider_name && { borderLeft: "10px solid red" }),
                }}
              />
              <StyledLabel className="user-label">Name*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
           {/* Service Provider Address */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
               style={{
                  ...(errors.service_provider_address && { borderLeft: "10px solid red" }),
                }}
                placeholder="Enter Service Provider Address"
                type="text"
                {...register("service_provider_address",{ required: true })}
              />
              <StyledLabel className="user-label">Address*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
           {/* Service Provider Code */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
               style={{
                  ...(errors.service_provider_code && { borderLeft: "10px solid red" }),
                }}
                placeholder="Enter Service Provider Code"
                type="text"
                {...register("service_provider_code",{ required: true })}
              />
              <StyledLabel className="user-label">Code*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
          <div className="d-flex" style={{ gap: "68px" }}>
            <div>
              <div className="uploadImageContainer d-flex gap-3 mb-3">
                <StyledFileInput
                  id="logoInput"
                  accept="image/*" 
                  type="file"
                  onChange={onLogoChange}
                  ref={logoInputRef}
                />
                <StyledUploadLabel htmlFor="logoInput" error={logoError}>
                  Upload Logo *
                </StyledUploadLabel>
                <SecondaryButton type="button" onClick={handleCancelLogo}>
                  Cancel
                </SecondaryButton>
              </div>
              {logoError && <ErrorText style={{color: "red"}}>*Logo is required</ErrorText>}
              {logoPreview && (
                <img
                  className="organizationLogo"
                  alt="logo-preview"
                  src={logoPreview}
                  style={{ minWidth: "280px", minHeight: "180px" }}
                />
              )}
            </div>

            <div>
              <div className="uploadImageContainer d-flex gap-3 mb-3">
                <StyledFileInput
                  id="signatureInput"
                  accept="image/*" 
                  type="file"
                  onChange={onSignatureChange}
                  ref={signatureInputRef}
                />
                <StyledUploadLabel htmlFor="signatureInput" error={signatureError}>
                  Upload Signature *
                </StyledUploadLabel>
                <SecondaryButton type="button" onClick={handleCancelSignature}>
                  Cancel
                </SecondaryButton>
              </div>
              {signatureError && <ErrorText style={{color: "red"}}>*Signature is required</ErrorText>}
              {signaturePreview && (
                <img
                  className="organizationLogo"
                  alt="signature-preview"
                  src={signaturePreview}
                  style={{ minWidth: "280px", minHeight: "180px" }}
                />
              )}
            </div>
          </div>
          
          
      </div>
<h5 style={{ marginBottom: "18px", marginTop: "10px" }}>
          Billing Address Details
        </h5>
        <div className="add-employee-form-container">
    {/* Billing Address */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Billing Address"
                type="text"
                {...register("billing_address",{ required: true })}
                style={{
                  ...(errors.billing_address && { borderLeft: "10px solid red" }),
                }}
              />
              <StyledLabel className="user-label">Billing Address*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
     {/* Billing City */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Billing City"
                type="text"
                {...register("billing_city",{ required: true })}
                 style={{
                  ...(errors.billing_city && { borderLeft: "10px solid red" }),
                }}
              />
              <StyledLabel className="user-label">Billing City*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
      {/* Billing Zipcode */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Billing Zipcode"
                type="text"
                {...register("billing_zipcode",{ required: true })}
                 style={{
                  ...(errors.billing_zipcode && { borderLeft: "10px solid red" }),
                }}
              />
              <StyledLabel className="user-label">Billing Zipcode*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
         {/* Billing State */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Billing State"
                type="text"
                {...register("billing_state",{ required: true })}
                style={{
                  ...(errors.billing_state && { borderLeft: "10px solid red" }),
                }}
              />
              <StyledLabel className="user-label">Billing State*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
             {/* Billing Country */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Billing Country"
                type="text"
                {...register("billing_country",{ required: true })}
                style={{
                  ...(errors.billing_country && { borderLeft: "10px solid red" }),
                }}
              />
              <StyledLabel className="user-label">Billing Country*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
        </div>
        <h5 style={{ marginBottom: "18px", marginTop: "15px" }}>
          Tax & Legal Information
        </h5>
        <div className="add-employee-form-container">
          {/* GSTIN */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter GSTIN"
                type="text"
                {...register("gstin", {
                  pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                  setValueAs: (value) => value?.trim(),
                  onChange: (e) => e.target.value = e.target.value.toUpperCase(),
                })}
                style={{
                  ...(errors.gstin && { borderLeft: "10px solid red" }),
                }}
              />
              <StyledLabel hasValue={true} className="user-label">GSTIN</StyledLabel>
              <Tooltip title="GSTIN Format: 00AAAAA0000A0Z0" arrow>
                <span style={{ position: "absolute", right: "10px", top: "5px" }}>
                  <IoIosInformationCircleOutline className="info-icon" />
                </span>
              </Tooltip>
            </StyledInputContainer>
          </StyledInputGroup>

          {/* PAN */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter PAN"
                type="text"
                {...register("pan", {
                  pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                  setValueAs: (value) => value?.trim(),
                  onChange: (e) => e.target.value = e.target.value.toUpperCase(),
                })}
                style={{
                  ...(errors.pan && { borderLeft: "10px solid red" }),
                }}
              />
              <StyledLabel hasValue={true} className="user-label">PAN</StyledLabel>
              <Tooltip title="PAN Format: AAAAA0000A" arrow>
                <span style={{ position: "absolute", right: "10px", top: "5px" }}>
                  <IoIosInformationCircleOutline className="info-icon" />
                </span>
              </Tooltip>
            </StyledInputContainer>
          </StyledInputGroup>
        </div>
        <h5 style={{ marginBottom: "18px", marginTop: "15px" }}>
          Banking Details
        </h5>
        <div className="add-employee-form-container"> 
        {/* Bank Name */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Bank Name"
                type="text"
                {...register("bank_name")}
              />
              <StyledLabel className="user-label">Bank Name</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup> 

          {/* Bank Branch */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Bank Branch"
                type="text"
                {...register("bank_branch")}
              />
              <StyledLabel className="user-label">Bank Branch</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          {/* Bank Account Number */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Bank Account Number"
                type="text"
                {...register("bank_account_number")}
              />
              <StyledLabel className="user-label">Bank Account Number</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          {/* Bank IFSC */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Bank IFSC"
                type="text"
                {...register("bank_ifsc")}
              />
              <StyledLabel className="user-label">Bank IFSC</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          {/* Bank SWIFT Code */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Bank SWIFT Code"
                type="text"
                {...register("bank_swift_code")}
              />
              <StyledLabel className="user-label">Bank SWIFT Code</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup> 
          <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect {...register("account_type")}>
              <option value="">---Select Account Type---</option>
              <option value="CAA">Current Account (Active)</option>
              <option value="CAD">Current Account (Dormant)</option>
            </StyledSelect>
            <StyledLabel className="user-label">Account Type</StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>
          <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect {...register("account_category")}>
              <option value="">---Select Account Category Schema Code---</option>
              <option value="CASIL">Current Account Sole Individual</option>
              <option value="CAPRO">Current Account Proprietorship</option>
              <option value="CAPRT">Current Account Partnership</option>
            </StyledSelect>
            <StyledLabel className="user-label">Account Category Schema Code</StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>
        </div>
      </div>
      {errorMsg && (
                <ErrorText className="mt-3">*{errorMsg}</ErrorText>
              )}

      {/* Buttons */}
      <div className="d-flex gap-2 justify-content-end mt-4">
        <SecondaryButton onClick={closeBtn}>Cancel</SecondaryButton>
        {canEdit && (
          <PrimaryButton onClick={validateAndSubmit} disabled={isLoading}>
            {isLoading ? (isEditMode ? "Updating..." : "Adding...") : (isEditMode ? "Update" : "Add")}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export default BillingDetails;